// BulkStatusUpdate.tsx
// Provides a UI for bulk updating the authorization status of selected orders in the OrderTable.
// Handles status update requests, error/success feedback, and clearing selected orders.

import { Button, Toast } from "@Forge/dist";
import { styled } from "@stitches/react";
import { useUpdateAuthStatusBulk } from "@/hooks/use-auth-status";
import { useEffect, useState } from "react";

/**
 * Props for BulkStatusUpdate component
 * @property ordersToUpdate - Array of case numbers to update
 * @property clearOrdersToUpdate - Callback to clear selected orders
 */
interface Props {
  ordersToUpdate: string[];
  clearOrdersToUpdate: () => void;
}

const BulkStatusUpdateWrapper = styled("div", {
  padding: "20px 18px",
  backgroundColor: "#EDEDFB",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const TextWrapper = styled("p", {
  margin: "0px !important",
  variants: {
    bold: {
      true: {
        fontWeight: "bold !important",
      },
    },
  },
});

/**
 * BulkStatusUpdate Component
 * Renders a toolbar for bulk updating the auth status of selected orders.
 * Handles update requests, error/success feedback, and clearing selection.
 * @param ordersToUpdate - Array of case numbers to update
 * @param clearOrdersToUpdate - Callback to clear selected orders
 */
const BulkStatusUpdate = ({ ordersToUpdate, clearOrdersToUpdate }: Props) => {
  const { isPending, isError, error, updateStatusBulk, isSuccess } =
    useUpdateAuthStatusBulk();
  const [toastData, setToastData] = useState<{
    message: string;
    header: string;
    type: "success" | "attention" | "info";
  } | null>(null);

  const handleUpdateOrders = () => {
    updateStatusBulk({
      caseNumbers: ordersToUpdate,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      clearOrdersToUpdate();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setToastData({
        message: `${error?.message || "An error occurred"}. Please try again.`,
        header: "Error",
        type: "attention",
      });
    }
  }, [isError]);

  return (
    <BulkStatusUpdateWrapper>
      <TextWrapper bold>{ordersToUpdate.length} Selected</TextWrapper>
      <TextWrapper>
        Change all to <b>Not Required</b>
      </TextWrapper>
      <Button variant="secondary" onClick={handleUpdateOrders}>
        {isPending ? "Updating Status..." : "Change Auth Status"}
      </Button>
      <Button variant="tertiary" onClick={clearOrdersToUpdate}>
        Deselect All
      </Button>
      {toastData && (
        <Toast
          id="bulk-toast"
          headerText={toastData?.header}
          alertType={toastData?.type}
          onDismiss={() => setToastData(null)}
        >
          <p>{toastData?.message}</p>
        </Toast>
      )}
    </BulkStatusUpdateWrapper>
  );
};

export default BulkStatusUpdate;
